import { useEffect, useRef, useState } from 'react';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useIntegrationsContext } from 'src/providers/IntegrationsProvider/Integrations.context';
import {
  eIntegrationStatus,
  eIntegrationType,
} from 'src/providers/IntegrationsProvider/Integrations.enums';
import { IntegrationsService } from 'src/providers/IntegrationsProvider/Integrations.service';
import { useModalsContext } from 'src/providers/ModalsProvider/Modals.context';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import { SyncConversationsEnum } from 'src/providers/SyncConversationsProvider/SyncConversations.enum';
import { useSyncConversationsContext } from 'src/providers/SyncConversationsProvider/SyncConversations.context';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { QR_TIMEOUT_MS } from './ConnectionModal.constant.ts';
import { tConnectionModalHook, tQrStatus } from './ConnectionModal.types.ts';
import { tSocketNewQr } from './QrCode/QrCode.types.ts';

export default function useConnectionModal(): tConnectionModalHook {
  const [qrStatus, setQrStatus] = useState<tQrStatus>('requesting');
  const [qrCode, setQrCode] = useState('');
  const [historyCheckAttempt, setHistoryCheckAttempt] = useState(0);
  // Blocks re-entrant retry() calls — a concurrent retry would fire a duplicate
  // POST /account (duplicate whatsapp_businesses row risk, INT-896 family).
  const retryInFlightRef = useRef(false);

  const { addAlert } = useToastContext();

  const { whatsappIntegration, setWhatsappIntegration } =
    useIntegrationsContext();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { setConnectionModalIsOpen, connectionModalIsOpen } =
    useModalsContext();
  const { setSyncConversationsStatus } = useModalsContext();
  const { setSelectedIntegration } = useSyncConversationsContext();
  const { newQREvent } = useSocketsContext();

  // The QR code is delivered asynchronously over the socket. Its arrival is the
  // terminal success signal: show it, and clear any prior 'awaiting'/'failed'
  // state (this also recovers gracefully from a QR that lands after the
  // watchdog already fired).
  newQREvent.useSubscription((value: tSocketNewQr) => {
    setQrCode(value.qr_code);
    setQrStatus('ready');
  });

  async function fetchAccountStatus() {
    if (!currentWorkspaceId) return;

    try {
      const response = await IntegrationsService.getAccountStatus({
        workspaceId: currentWorkspaceId,
      });

      const { data } = response;
      if (!data) {
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'ConnectionModal.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Estado inválido en la respuesta',
          new Error('Invalid state'),
          { responseData: data },
        );
        return;
      }

      // INT-1007: mapping must cover every numeric AccountState the wws state
      // machine can emit. Previously missing `4 = QR_RECEIVED` made
      // fetchAccountStatus a no-op for that state (the row's state never
      // reached the useEffect below), and `6` was mistakenly mapped to
      // CONNECTING — so a successful connection never auto-closed the modal.
      // Both gaps were latent until INT-993 made the BFF parser honor
      // `state.State` from wws, at which point AUTH_REQUESTED started flowing
      // through to the FE and the stuck QR loader surfaced in production.
      const stateMapping: Record<number, eIntegrationStatus> = {
        1: eIntegrationStatus.NO_ACCOUNT,
        2: eIntegrationStatus.ACCOUNT_CREATED,
        3: eIntegrationStatus.CONNECTION_REQUEST,
        4: eIntegrationStatus.AUTH_REQUESTED,
        5: eIntegrationStatus.CONNECTING,
        6: eIntegrationStatus.CONNECTED,
        7: eIntegrationStatus.BANNED,
      };

      const newState = stateMapping[data.state];
      if (newState) {
        setWhatsappIntegration({
          ...whatsappIntegration,
          state: newState,
        });
      } else {
        LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'ConnectionModal.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Estado no reconocido',
          { responseData: data },
        );
      }
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ConnectionModal.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al obtener el estado de la cuenta',
        error as unknown as Error,
      );
    }
  }

  async function createAccount() {
    if (!currentWorkspaceId) return;
    // Entering a QR request always resets the panel to a loading phase, which
    // clears any prior 'failed' so a re-trigger after a timeout no longer
    // leaves the failure screen pinned over an in-flight request.
    setQrStatus('requesting');
    try {
      await IntegrationsService.createChatDaddyAccount({
        workspace_id: currentWorkspaceId,
        options: {
          getMessageHistory: true,
        },
      });
      setWhatsappIntegration({
        ...whatsappIntegration,
        state: eIntegrationStatus.ACCOUNT_CREATED,
      });
      // Stays 'requesting': the ACCOUNT_CREATED transition above re-runs the
      // integration-state effect, which calls generateQrCode() and advances
      // the panel to 'awaiting'.
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ConnectionModal.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error en ConnectionModal',
        error as unknown as Error,
      );
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message ||
          'Ocurrió un error al crear y conectar  la cuenta con leadsales',
      });
      setQrStatus('failed');
    }
  }

  async function generateQrCode() {
    if (!currentWorkspaceId) return;
    setQrStatus('requesting');
    try {
      await IntegrationsService.generateQrCode({
        workspaceId: currentWorkspaceId,
      });
      // The QR request is out; hand off to the socket-delivery watchdog —
      // unless the QR already arrived over the socket during the await.
      setQrStatus((prev) => (prev === 'requesting' ? 'awaiting' : prev));
    } catch (error) {
      // Post-INT-937 the backend returns 404 when the provider account is
      // gone — surface the retry state instead of an infinite spinner.
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ConnectionModal.hook',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al generar el código QR',
        error as unknown as Error,
      );
      setQrStatus('failed');
    }
  }

  async function retry() {
    if (!currentWorkspaceId) return;
    // Block re-entry: a concurrent retry would issue a duplicate POST /account.
    if (retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    setQrStatus('requesting');
    try {
      if (whatsappIntegration.state === eIntegrationStatus.CONNECTED) {
        setHistoryCheckAttempt((attempt) => attempt + 1);
        return;
      }
      // Best-effort self-heal: POST /account makes the backend probe-then-
      // replace a stale/dead account row (INT-937). A 409 (account already
      // healthy) or any other error here is non-fatal — the QR request below
      // is the real signal for whether a code can be produced.
      await IntegrationsService.createChatDaddyAccount({
        workspace_id: currentWorkspaceId,
        options: {
          getMessageHistory: true,
        },
      }).catch((error: unknown) => {
        LLog.warn(
          {
            component: LOG_COMPONENTS.UI,
            method: 'ConnectionModal.hook.retry',
            operation: LOG_OPERATIONS.ERROR,
          },
          'POST /account during QR retry failed (non-fatal)',
          { error: String(error) },
        );
      });
      await IntegrationsService.generateQrCode({
        workspaceId: currentWorkspaceId,
      });
      // The QR request is out. Advancing to 'awaiting' here re-arms the
      // watchdog with a fresh window, so the two awaited calls above never
      // consume the QR-delivery budget — unless the QR already arrived.
      setQrStatus((prev) => (prev === 'requesting' ? 'awaiting' : prev));
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ConnectionModal.hook.retry',
          operation: LOG_OPERATIONS.ERROR,
        },
        'Error al reintentar el código QR',
        error as unknown as Error,
      );
      setQrStatus('failed');
    } finally {
      retryInFlightRef.current = false;
    }
  }

  useEffect(() => {
    fetchAccountStatus();
  }, [currentWorkspaceId]);

  // INT-986: ACCOUNT_CREATED / DISCONNECTED / CONNECTION_REQUEST / CONNECTING
  // all route to generateQrCode(). The wws state machine can leave an account
  // in either of the intermediate states (CONNECTION_REQUEST, CONNECTING) when
  // a previous QR flow was interrupted (modal closed mid-scan, ChatDaddy
  // session never completed). Treat them as "QR flow stalled — request a
  // fresh QR" so the modal recovers on its own instead of relying on the
  // watchdog firing `failed` after QR_TIMEOUT_MS and the user clicking
  // "Reintentar". Comment lives here (not between the case labels) so ESLint's
  // `no-fallthrough` rule sees the labels as a single uninterrupted
  // fall-through group.
  //
  // INT-1007: AUTH_REQUESTED was the missing case here. Before the BFF parser
  // fix in INT-993 the BFF collapsed wws state=4 (QR_RECEIVED) into
  // ACCOUNT_CREATED, so this switch never saw AUTH_REQUESTED and the gap was
  // latent. After INT-993, AUTH_REQUESTED reaches the modal and — without a
  // case — the switch is a silent no-op: generateQrCode never fires, the
  // panel stays at qrStatus='requesting' until QR_TIMEOUT_MS, and the user
  // sees the "Reintentar" failure screen for a flow that should have worked.
  useEffect(() => {
    switch (whatsappIntegration.state) {
      case eIntegrationStatus.NO_ACCOUNT:
        createAccount();
        break;
      case eIntegrationStatus.ACCOUNT_CREATED:
      case eIntegrationStatus.DISCONNECTED:
      case eIntegrationStatus.CONNECTION_REQUEST:
      case eIntegrationStatus.AUTH_REQUESTED:
      case eIntegrationStatus.CONNECTING:
        generateQrCode();
        break;
      case eIntegrationStatus.CONNECTED:
        // The completion effect refreshes history availability before closing.
        break;
      case eIntegrationStatus.BANNED:
        setConnectionModalIsOpen(false);
        break;
    }
  }, [whatsappIntegration.state]);

  useEffect(() => {
    if (
      whatsappIntegration.state !== eIntegrationStatus.CONNECTED ||
      !currentWorkspaceId ||
      !connectionModalIsOpen
    )
      return;
    let cancelled = false;
    // GET /qr re-arms history on relinking. The flag read before the QR was
    // generated belongs to the previous pairing and cannot decide this offer.
    IntegrationsService.getAccountStatus({ workspaceId: currentWorkspaceId })
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) throw new Error('Invalid account status');
        if (data.messageSync) {
          setSelectedIntegration(eIntegrationType.WHATSAPP_WEB);
          setSyncConversationsStatus(SyncConversationsEnum.QUESTION);
        }
        setConnectionModalIsOpen(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        LLog.error(
          {
            component: LOG_COMPONENTS.UI,
            method: 'ConnectionModal.hook',
            operation: LOG_OPERATIONS.ERROR,
          },
          'Error al consultar el historial disponible después de conectar',
          error as Error,
        );
        setQrStatus('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [
    whatsappIntegration.state,
    currentWorkspaceId,
    connectionModalIsOpen,
    historyCheckAttempt,
  ]);

  // Watchdog: while the panel is in a loading phase ('requesting' = a QR HTTP
  // request is in flight, 'awaiting' = the request resolved and the socket
  // push is pending), bound the wait. If no QR has arrived within
  // QR_TIMEOUT_MS the request has silently failed (e.g. the provider account
  // was deleted — INT-937) and the retry state is surfaced instead of
  // spinning forever. Because qrStatus is a dependency, the
  // 'requesting' -> 'awaiting' transition re-runs this effect and re-arms a
  // fresh timer, so the socket wait always gets a full window even when the
  // preceding HTTP call(s) were slow. currentWorkspaceId is a dependency so a
  // workspace switch re-arms the timer (and keeps the warn log accurate).
  useEffect(() => {
    if (qrStatus !== 'requesting' && qrStatus !== 'awaiting') return;
    if (!connectionModalIsOpen) return;

    const timer = setTimeout(() => {
      LLog.warn(
        {
          component: LOG_COMPONENTS.UI,
          method: 'ConnectionModal.hook.timeout',
          operation: LOG_OPERATIONS.ERROR,
        },
        'El código QR no llegó dentro del tiempo de espera',
        { workspaceId: currentWorkspaceId },
      );
      // Functional update: only fail if still loading — a QR that arrived in
      // the final moment (status already 'ready') must not be overwritten.
      setQrStatus((prev) =>
        prev === 'requesting' || prev === 'awaiting' ? 'failed' : prev,
      );
    }, QR_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [qrStatus, connectionModalIsOpen, currentWorkspaceId]);

  return { qrStatus, qrCode, retry };
}
